import { Box } from '@mui/material';
import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import { PrimaryButtonLG } from '@/components/shared/Button';
import { SMBold, SMRegular, SMSemiBold } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { BankRecordsSyncResult } from '@/services/PaymentsServiceV2';

export interface BankSyncSummaryDialogProps {
    open: boolean;
    summary: BankRecordsSyncResult | null;
    handleClose: () => void;
}

const AccountNameList = ({ names }: { names: string[] }) => (
    <Box component="ul" sx={{ m: 0, pl: 3 }}>
        {names.map((name) => (
            <li key={name}>
                <SMRegular>{name}</SMRegular>
            </li>
        ))}
    </Box>
);

// Surfaces the per-account breakdown of a manual sync so an operator can see
// exactly which house accounts were skipped or failed — the toast only carries
// the overall state.
const BankSyncSummaryDialog = ({
    open,
    summary,
    handleClose
}: BankSyncSummaryDialogProps) => {
    const { t } = useTranslation();

    if (!summary) {
        return null;
    }

    const hasFailed = summary.failedHouseAccounts.length > 0;
    const hasSkipped = summary.skippedHouseAccounts.length > 0;

    return (
        <StyledDialog open={open} onClose={handleClose}>
            <StyledDialogContainer style={{ width: '440px', padding: '24px' }}>
                <SMSemiBold mb={2}>
                    {t('transaction_monitoring.sync_summary_title')}
                </SMSemiBold>

                <SMRegular mb={2}>
                    {t('transaction_monitoring.sync_summary_counts', {
                        downloaded: summary.downloadedCount,
                        skipped: summary.skippedCount,
                        failed: summary.failedCount
                    })}
                </SMRegular>

                {hasFailed && (
                    <Box mb={2}>
                        <SMBold mb={0.5}>
                            {t('transaction_monitoring.sync_summary_failed')}
                        </SMBold>
                        <AccountNameList names={summary.failedHouseAccounts} />
                    </Box>
                )}

                {hasSkipped && (
                    <Box mb={2}>
                        <SMBold mb={0.5}>
                            {t('transaction_monitoring.sync_summary_skipped')}
                        </SMBold>
                        <AccountNameList names={summary.skippedHouseAccounts} />
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 1
                    }}
                >
                    <PrimaryButtonLG
                        size="large"
                        style={{ height: '39px' }}
                        onClick={handleClose}
                    >
                        <SMBold textTransform={'none'}>{t('close')}</SMBold>
                    </PrimaryButtonLG>
                </Box>
            </StyledDialogContainer>
        </StyledDialog>
    );
};

export default BankSyncSummaryDialog;
